import { Fragment } from "react";

import { PageContainer, SpinnerCenter } from "@shapr3d/frontend-lib";
import { useParams } from "react-router-dom";

import CTARow from "../components/CTARow/CTARow";
import { CTARowDivider } from "../components/CTARowDivider/CTARowDivider";
import { PricingCardsSection } from "../components/PricingCardsSection/PricingCardsSection";
import { PurchaseNotice } from "../components/PurchaseNotice/PurchaseNotice";
import { IndividualSubscriptionInfo } from "../components/TeamSubscriptions/IndividualSubscriptionInfo/IndividualSubscriptionInfo";
import { PaddleOneTimeSubscriptionInfo } from "../components/TeamSubscriptions/PaddleOneTimeSubscriptionInfo/PaddleOneTimeSubscriptionInfo";
import { SeatBasedSubscriptionInfo } from "../components/TeamSubscriptions/SeatBasedSubscriptionInfo/SeatBasedSubscriptionInfo";
import UnderMaintenanceOrChildren from "../components/UnderMaintenanceOrChildren/UnderMaintenanceOrChildren";
import { WelcomeModal } from "../components/WelcomeModal/WelcomeModal";
import { useChinaUser } from "../hooks/useChinaUser";
import { useHasStudioPricing } from "../hooks/usePriceConfig";
import { useTeamSubscriptions } from "../hooks/useTeams";
import useTranslation from "../hooks/useTranslation";
import { SubscriptionType } from "../types";

const TeamSubscriptionPage = () => {
  const { isStudioTier, isPending } = useHasStudioPricing();

  const { t, m } = useTranslation();
  const { teamID } = useParams();
  const { isChinaUser, isLoading: isChinaUserLoading } = useChinaUser();

  const { data: teamSubscriptionData, isLoading: isTeamSubscriptionLoading } =
    useTeamSubscriptions(teamID);

  if (
    isChinaUserLoading ||
    isTeamSubscriptionLoading ||
    !teamSubscriptionData ||
    !teamID ||
    isPending
  ) {
    return <SpinnerCenter />;
  }

  const hasNoSubscription =
    !teamSubscriptionData.creditBasedTeamSubscription &&
    !teamSubscriptionData.seatBasedTeamSubscription &&
    !teamSubscriptionData.individualSubscriptions;

  return (
    <UnderMaintenanceOrChildren>
      <PageContainer
        title={
          hasNoSubscription && isStudioTier
            ? t(m.TeamSubscriptionPage.StudioPricingTitle)
            : isChinaUser
              ? t(m.TeamSubscriptionPage.LicenseTitle)
              : t(m.TeamSubscriptionPage.SubscriptionTitle)
        }
      >
        {hasNoSubscription && (
          <>
            {!isStudioTier && (
              <>
                <CTARow
                  title={
                    isChinaUser
                      ? t(m.TeamSubscriptionPage.LicenseTitle)
                      : t(m.TeamSubscriptionPage.SubscriptionTitle)
                  }
                  text={t(m.TeamSubscriptionPage.BasicPlanText)}
                />
                <CTARowDivider />
              </>
            )}
            {isStudioTier ? (
              <PricingCardsSection />
            ) : (
              <PurchaseNotice isChinaVariant={isChinaUser} />
            )}
          </>
        )}

        {teamSubscriptionData.individualSubscriptions && (
          <>
            {teamSubscriptionData.individualSubscriptions.map((subscription) => (
              <Fragment key={subscription.id}>
                {subscription.type === SubscriptionType.PaddleOneTime ? (
                  <PaddleOneTimeSubscriptionInfo subscription={subscription} />
                ) : (
                  <IndividualSubscriptionInfo subscription={subscription} />
                )}
                <CTARowDivider />
              </Fragment>
            ))}
          </>
        )}

        {teamSubscriptionData.seatBasedTeamSubscription && (
          <SeatBasedSubscriptionInfo
            subscription={teamSubscriptionData.seatBasedTeamSubscription}
            teamID={teamID}
          />
        )}

        {teamSubscriptionData.creditBasedTeamSubscription &&
          // NOTE: We are not rendering it yet, since they are only members of the team, and currently we are only showing the members page for them
          null}
        <WelcomeModal />
      </PageContainer>
    </UnderMaintenanceOrChildren>
  );
};

export default TeamSubscriptionPage;
